import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUsers, FiHome, FiFileText, FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { SearchResult } from './global-search';

interface PartnerResultsProps {
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  selectedIndex: number;
}

export const PartnerResults: FC<PartnerResultsProps> = ({ results, onResultClick, selectedIndex }) => {
  const { t } = useTranslation();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Partner-specific mock data
  const partnerMockResults: SearchResult[] = [
    // Analysts
    {
      id: 'partner-1',
      type: 'person',
      title: 'Ana Silva',
      subtitle: 'Analista',
      description: 'ana.silva@partner.com',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      href: '/analistas/1',
      metadata: { type: 'analyst', status: 'active', credits: 150 }
    },
    {
      id: 'partner-2',
      type: 'person',
      title: 'Carlos Santos',
      subtitle: 'Analista',
      description: 'carlos.santos@partner.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      href: '/analistas/2',
      metadata: { type: 'analyst', status: 'active', credits: 200 }
    },
    // People
    {
      id: 'partner-3',
      type: 'person',
      title: 'João Pereira',
      subtitle: 'Candidato',
      description: 'joao.pereira@email.com',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      href: '/pessoas/3',
      metadata: { type: 'candidate', status: 'active', assessments: 3 }
    },
    // Certificates
    {
      id: 'partner-4',
      type: 'assessment',
      title: 'Certificado - Ana Silva',
      subtitle: 'Status: Emitido',
      description: 'Certificado de conclusão de avaliação',
      href: '/certificados/4',
      metadata: { type: 'certificate', status: 'issued', date: '2024-03-15' }
    },
    // Invoices
    {
      id: 'partner-5',
      type: 'assessment',
      title: 'Fatura #2024-001',
      subtitle: 'Status: Paga',
      description: 'Compra de créditos - 500 unidades',
      href: '/invoices/5',
      metadata: { type: 'invoice', status: 'paid', amount: 500 }
    },
    // Integrations
    {
      id: 'partner-6',
      type: 'group',
      title: 'Integração - LinkedIn',
      subtitle: 'Status: Ativa',
      description: 'Sincronização de dados do LinkedIn',
      href: '/integracoes/6',
      metadata: { type: 'integration', status: 'active', platform: 'linkedin' }
    }
  ];

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return <FiFileText />;
      case 'person':
        return <FiUsers />;
      case 'company':
        return <FiHome />;
      case 'group':
        return <FiList />;
      default:
        return <FiUsers />;
    }
  };

  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return 'blue';
      case 'person':
        return 'green';
      case 'company':
        return 'purple';
      case 'group':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return t('search.assessment');
      case 'person':
        return t('search.person');
      case 'company':
        return t('search.company');
      case 'group':
        return t('search.group');
      default:
        return type;
    }
  };

  // Combine mock results with actual results for partner
  const allResults = [...partnerMockResults, ...results];

  if (allResults.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color="gray.500">{t('search.no_results')}</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={0} align="stretch">
      {allResults.map((result, index) => {
        const isSelected = index === selectedIndex;
        return (
          <Box
            key={result.id}
            px={4}
            py={3}
            cursor="pointer"
            bg={isSelected ? hoverBg : 'transparent'}
            _hover={{ bg: hoverBg }}
            onClick={() => onResultClick(result)}
            borderBottom={index < allResults.length - 1 ? '1px solid' : 'none'}
            borderColor={useColorModeValue('gray.100', 'gray.600')}
            borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
            borderLeftColor="blue.500"
          >
            <HStack spacing={3} align="start">
              {result.avatar ? (
                <Avatar size="sm" src={result.avatar} name={result.title} />
              ) : (
                <Box
                  p={2}
                  borderRadius="md"
                  bg={`${getTypeColor(result.type)}.100`}
                  color={`${getTypeColor(result.type)}.600`}
                >
                  {getTypeIcon(result.type)}
                </Box>
              )}
              <Box flex={1}>
                <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                  {result.title}
                </Text>
                {result.subtitle && (
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {result.subtitle}
                  </Text>
                )}
                {result.description && (
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {result.description}
                  </Text>
                )}
              </Box>
              <Badge size="sm" colorScheme={getTypeColor(result.type)} variant="subtle">
                {getTypeLabel(result.type)}
              </Badge>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}; 